<template>
  <section class="schedule-bell">
    <div v-if="loading" class="schedule-bell__loading" />

    <EmptyStateNotice
      v-else-if="!normalizedBells.length"
      :text="t('scheduleEmpty')"
      variant="info"
    />

    <div v-else class="bell__head">
      <div class="bell_body_main" v-for="(bellItem, bellIndex) in normalizedBells" :key="`bell-${bellIndex}`">
        <article class="week bell__body">
          <div class="title">
            {{ quarterName(bellIndex) }}
            <span class="ml-1">{{ t("shift_text").toLowerCase() }}</span>
          </div>
          <div class="subtitle">
            {{ t("mon_short_text") }}, {{ t("tue_short_text") }},
            {{ t("wed_short_text") }}, {{ t("thu_short_text") }},
            {{ t("fri_short_text") }}, {{ t("sat_short_text") }}
          </div>

          <div
            v-for="(bell, index) in bellItem._bells"
            :key="`bell-item-${bellIndex}-${index}`"
            class="bell-item"
          >
            <div class="bell-item-body">
              <span class="bell-item-subjects">
                {{ subjectEnum(index) }} <span class="ml-1">{{ t("lesson") }}</span>
              </span>
              <span class="bell-item-time">
                {{ timeFormat(bell.start_time) }} - {{ timeFormat(bell.end_time) }}
              </span>
            </div>
            <div
              v-if="
                bellItem._bells.length !== index + 1 &&
                getPauseTime(bell.end_time, bellItem._bells[index + 1].start_time) > 0
              "
              class="bell-item-sub-text"
            >
              {{ breakText[0] }}
              {{ getPauseTime(bell.end_time, bellItem._bells[index + 1].start_time) }}
              {{ breakText[1] }}
            </div>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import EmptyStateNotice from "@/components/common/EmptyStateNotice.vue";
import { getPauseTime } from "@/utils/schedule";

const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  bells: {
    type: Array,
    default: () => [],
  },
});

const { t, tm } = useI18n();

const normalizedBells = computed(() =>
  (Array.isArray(props.bells) ? props.bells : [])
    .map((item) => ({
      ...item,
      _bells: Array.isArray(item?._bells)
        ? item._bells
        : Array.isArray(item?.bells)
        ? item.bells
        : [],
    }))
    .filter((item) => item._bells.length)
);

const fourthNameList = computed(() => {
  const raw = tm("fourthName");
  return Array.isArray(raw) ? raw : [];
});

const subjectEnumList = computed(() => {
  const raw = tm("subjectEnum");
  return Array.isArray(raw) ? raw : [];
});
const breakText = computed(() => {
  const raw = tm("break_text");
  return Array.isArray(raw) ? raw : [];
});

function quarterName(index) {
  return fourthNameList.value[index] || `${index + 1}`;
}

function subjectEnum(index) {
  return subjectEnumList.value[index] || `${index + 1}`;
}

function timeFormat(value) {
  if (!value) return "";
  const parts = String(value).split(":");
  return `${parts[0] || "00"}:${parts[1] || "00"}`;
}
</script>

<style scoped>
.schedule-bell {
  padding: 0;
}

.bell__head {
  padding: 0;
}

.bell_body_main {
  border-radius: 0.5rem;
  overflow: hidden;
  border: none;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.1);
}

.bell__body {
  text-align: center;
  color: #828282;
  font-family: Roboto, sans-serif;
  overflow: auto !important;
  padding: 16px;
  border: 1px solid #e0e0e0;
  box-sizing: border-box;
  box-shadow: inset 7px 10px 12px #f0f0f0;
}

.title {
  margin: 0;
  font-size: 20px;
  font-weight: bold !important;
}

.subtitle {
  margin: 0;
  font-size: 13px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e0e0e0;
  font-weight: normal !important;
}

.bell-item {
  display: flex;
  align-items: center;
  position: relative;
  justify-content: center;
  border-bottom: 1px dashed #36b453;
  padding: 0 6px 10px 6px;
}

.bell-item-body {
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
}

.bell-item-subjects {
  color: #4f4f4f;
  font-size: 14px;
  font-weight: 600;
  margin-top: 15px;
}

.bell-item-time {
  font-size: 20px;
  font-weight: normal !important;
  font-family: Roboto, sans-serif;
}

.bell-item-sub-text {
  text-align: center;
  position: absolute;
  top: 83%;
  font-size: 12px;
  font-family: Roboto, sans-serif;
  color: #36b453;
  background-color: #fff;
  padding: 5px;
}

.bell-item:last-child {
  border-bottom: none;
}

.bell__body::-webkit-scrollbar {
  width: 3px;
  height: 3px;
}

.bell__body::-webkit-scrollbar-thumb {
  background: #d2d2d4;
  border-radius: 0;
}

.bell__body::-webkit-scrollbar-thumb:hover {
  background: #ebebf6;
}

.bell__body::-webkit-scrollbar-track {
  background: #fff;
  border-radius: 0;
  box-shadow: inset 7px 10px 12px #f0f0f0;
}

@media screen and (min-width: 768px) {
  .bell__head {
    padding: 24px;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
  }

  .bell__body {
    background: #fff;
    border: 1px solid #e0e0e0;
    box-sizing: border-box;
    border-radius: 8px;
    min-width: 336px;
    height: 554px;
    padding: 16px;
    margin: 8px 8px;
    overflow: auto;
    box-shadow: none !important;
  }
}

@media screen and (max-width: 768px) {
  .bell__body {
    width: 100%;
    border: none !important;
    box-shadow: none !important;
    padding-top: 15px !important;
  }
}
</style>
